
import React from "react";
import { connect } from "react-redux";
import { Col } from "react-bootstrap";
import PropTypes from "prop-types";

import CsLineIcons from "../../../cs-line-icons";
import DataPanel from "../../../layout/data-panel";
import Table from "../../../layout/table";

import { format as formatDate } from "../../../utils/date";
import { currency as formatCurrency } from "../../../utils/number";
 
import { table as getInvoices, file as getFile } from "../../../actions/invoices";


class History extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            page: 0,
            size: 10,
            search: "",
            order: "createdAt",
            index: 0,
            direction: "DESC",
            data: [],
            pages: 0,
            total: 0,
            loading: true,
            error: "",
            downloading: null,
            view: "table",
            from: '2024-01-01',
            to: '2099-12-31'
        };
    }

    componentDidMount = () => {
        this.fetchData();
    }

    fetchData = async () => {
        this.setState({ error: "", loading: true });
        const result = await this.props.getInvoices(
            this.state.page,
            this.state.size,
            this.state.from,
            this.state.to,
            this.state.order,
            this.state.direction,
            this.state.search,
        );
        const data = Array.isArray(result) ? result : [];
        const total = Number(data[0]?.count || 0);

        this.setState({
            data,
            error: Array.isArray(result) ? "" : "Billing history could not be loaded.",
            loading: false,
            pages: total ? Math.ceil(total / Number(this.state.size)) : 0,
            total,
        });
    }

    get headers() {
        return [
            {
                header: "#",
                sortable: true,
                source: "number",
                className: "w-15"
            },
            {
                header: "Your Key",
                sortable: true,
                source: "LicenseeProduct.key",
                className: "w-30"
            },
            {
                header: "Amount",
                sortable: true,
                source: "subtotal",
                className: "w-20 text-end",
                dataClassName: "text-end",
                onFormat: this.onCurrencyFormat
            },
            {
                header: "Created",
                sortable: true,
                source: "createdAt",
                className: "w-25",
                onFormat: this.onDateFormat
            },
            {
                header: "",
                sortable: false,
                source: "uuid",
                className: "w-10 text-end",
                dataClassName: "text-end",
                onRender: this.onLinkFormat
            }
        ]
    }

    onCurrencyFormat = (e, row) => {
        return formatCurrency(Number(e));
    }

    onDateFormat = (e) => {
        return formatDate(e, true);
    }

    onItemClick = async (uuid) => {
        this.setState({ downloading: uuid });
        try {
            const __invoice = await this.props.getFile(uuid);
            if (!__invoice?.content) return;
            const linkSource = `data:application/pdf;base64,${__invoice.content}`;
            const link = document.createElement("a");

            link.href = linkSource;
            link.setAttribute(
                'download',
                __invoice.name,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } finally {
            this.setState({ downloading: null });
        }
    }

    onLinkFormat = (uuid, invoice) => (
        <button
            type="button"
            className="licensee-data-table-icon-button"
            aria-label="Download invoice"
            disabled={!invoice?.["File.uuid"] || this.state.downloading === uuid}
            title={invoice?.["File.uuid"] ? "Download invoice" : "PDF receipt is not available"}
            onClick={() => this.onItemClick(uuid)}
        >
            <CsLineIcons icon="download" size="17" />
        </button>
    );

    onColumnClick = (e) => {
        const __array = this.headers;
        const __index = __array.findIndex((item) => {
            return item.header === e.header && item.source === e.source;
        });

        this.setState({
            ...this.state,
            index: __index,
            direction: this.state.index === __index && this.state.direction === "DESC" ? "ASC" : "DESC",
            order: __array[__index].source,
            page: 0
        }, this.fetchData);
    }
    
    onSearch = (e) => {
        this.setState({
            ...this.state,
            search: e
        }, this.fetchData);
    }

    onSize = (e) => {
        this.setState({
            ...this.state,
            page: 0,
            size: Number(e)
        }, this.fetchData);
    }

    onPageChange = (e) => {
        this.setState({
            ...this.state,
            page: Number(e)
        }, this.fetchData);
    }

    onViewChange = (view) => {
        this.setState({ view });
    }

    get pageTotal() {
        return this.state.data.reduce(
            (total, invoice) => total + Number(invoice.subtotal ?? 0) + Number(invoice.tax ?? 0),
            0,
        );
    }

    renderInvoiceCard = (invoice) => (
        <article className="licensee-data-card" key={invoice.uuid}>
            <div className="licensee-data-card-title">
                <strong>Invoice #{invoice.number}</strong>
                <button
                    type="button"
                    className="licensee-data-table-icon-button"
                    aria-label={`Download invoice ${invoice.number}`}
                    disabled={!invoice["File.uuid"] || this.state.downloading === invoice.uuid}
                    title={invoice["File.uuid"] ? "Download invoice" : "PDF receipt is not available"}
                    onClick={() => this.onItemClick(invoice.uuid)}
                >
                    <CsLineIcons icon="download" size="17" />
                </button>
            </div>
            <div className="licensee-data-card-detail">
                <span>Your Key</span>
                <strong>{invoice["LicenseeProduct.key"] || "Not assigned"}</strong>
            </div>
            <div className="licensee-data-card-detail">
                <span>Amount</span>
                <strong>{this.onCurrencyFormat(invoice.subtotal)}</strong>
            </div>
            <div className="licensee-data-card-detail">
                <span>Created</span>
                <strong>{this.onDateFormat(invoice.createdAt)}</strong>
            </div>
        </article>
    );

    render() {
        return (
            <Col xs="12">
                <DataPanel
                    icon="file-text"
                    label="Billed on this page"
                    value={formatCurrency(this.pageTotal)}
                    description={`Across ${this.state.total} ${this.state.total === 1 ? "transaction" : "transactions"}`}
                    view={this.state.view}
                    onViewChange={this.onViewChange}
                >
                    <Table
                        ariaLabel="Billing history"
                        headers={this.headers}
                        page={this.state.data}
                        sort={{ index: this.state.index, direction: this.state.direction }}
                        size={this.state.size}
                        index={this.state.page}
                        pages={this.state.pages}
                        total={this.state.total}
                        loading={this.state.loading}
                        emptyTitle={this.state.error || "No billing history yet"}
                        emptyDescription={this.state.error ? "Try refreshing this page." : "Completed invoices will appear here."}
                        variant="licensee"
                        view={this.state.view}
                        renderCard={this.renderInvoiceCard}
                        onColumnClick={this.onColumnClick}
                        onPageChange={this.onPageChange}
                    />
                </DataPanel>
            </Col>
        );
    }
}

History.propTypes = {
    auth: PropTypes.object.isRequired
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth,
        invoices: state.invoice.list,
        page: state.page,
    });
};

export { History };
export default connect(mapStateToProps, { getInvoices, getFile })(History);
